import { AfterViewInit, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { VendorTypeService } from './vendor-type.service';
import { UpsertVendorTypeRequest, VendorType } from './vendor-type.types';
import { CatalogCrudModalComponent } from '../shared/components/catalog-crud-modal';
import { CatalogDeleteConfirmModalComponent } from '../shared/components/catalog-delete-confirm-modal';
import { CatalogPageCardComponent } from '../shared/components/catalog-page-card';
import { CatalogTableToolbarComponent } from '../shared/components/catalog-table-toolbar';
import { CatalogDatatableController, DatatableViewStateSnapshot } from '../shared/catalog-datatable.controller';
import { CatalogModalController } from '../shared/catalog-modal.controller';

interface VendorTypeFormValue {
  code: string;
  name: string;
  description: string;
  isActive: boolean;
}

type VendorTypeFormControlName = keyof VendorTypeFormValue;

@Component({
  selector: 'app-vendor-type',
  imports: [
    ReactiveFormsModule,
    CatalogPageCardComponent,
    CatalogTableToolbarComponent,
    CatalogCrudModalComponent,
    CatalogDeleteConfirmModalComponent,
  ],
  templateUrl: './vendor-type.html',
  styleUrl: './vendor-type.css',
})
export class VendorTypeComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly codeMaxLength = 30;
  private readonly nameMaxLength = 100;
  private readonly descriptionMaxLength = 200;
  private readonly datatableId = 'kt_vendor_type_datatable';
  private readonly createModalId = 'create_vendor_type_modal';
  private readonly editModalId = 'edit_vendor_type_modal';
  private readonly deleteConfirmModalId = 'delete_vendor_type_confirm_modal';
  private readonly formBuilder = inject(FormBuilder);
  private readonly datatableController = new CatalogDatatableController(this.datatableId);
  private readonly modalController = new CatalogModalController();

  private createModalObserver?: MutationObserver;
  private editModalObserver?: MutationObserver;

  protected readonly createVendorTypeForm = this.formBuilder.nonNullable.group({
    code: ['', [Validators.required, Validators.maxLength(this.codeMaxLength)]],
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength)]],
    description: ['', [Validators.maxLength(this.descriptionMaxLength)]],
    isActive: [true],
  });
  protected readonly editVendorTypeForm = this.formBuilder.nonNullable.group({
    code: ['', [Validators.required, Validators.maxLength(this.codeMaxLength)]],
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength)]],
    description: ['', [Validators.maxLength(this.descriptionMaxLength)]],
    isActive: [true],
  });
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly vendorTypes = signal<VendorType[]>([]);
  protected readonly isCreating = signal(false);
  protected readonly createFormSubmitted = signal(false);
  protected readonly createFormError = signal('');
  protected readonly editingVendorTypeId = signal<number | null>(null);
  protected readonly isUpdating = signal(false);
  protected readonly editFormSubmitted = signal(false);
  protected readonly editFormError = signal('');
  protected readonly deletingVendorTypeId = signal<number | null>(null);
  protected readonly pendingDeleteVendorTypeId = signal<number | null>(null);
  protected readonly alertVariant = signal<'success' | 'destructive' | null>(null);
  protected readonly alertTitle = signal('');
  protected readonly alertMessage = signal('');
  protected readonly hasAlert = computed(() => this.alertVariant() !== null);

  constructor(private readonly vendorTypeService: VendorTypeService) {}

  ngOnInit(): void {
    this.loadVendorTypes();
  }

  ngAfterViewInit(): void {
    this.createModalObserver = this.modalController.observeClose(this.createModalId, () => {
      this.resetCreateVendorTypeForm();
    });
    this.editModalObserver = this.modalController.observeClose(this.editModalId, () => {
      this.resetEditVendorTypeForm();
    });
  }

  ngOnDestroy(): void {
    this.createModalObserver?.disconnect();
    this.editModalObserver?.disconnect();
  }

  protected shouldShowCreateRequiredError(controlName: VendorTypeFormControlName): boolean {
    const control = this.createVendorTypeForm.controls[controlName];
    return this.createFormSubmitted() && !!control.errors?.['required'];
  }

  protected shouldShowCreateMaxLengthError(controlName: VendorTypeFormControlName): boolean {
    const control = this.createVendorTypeForm.controls[controlName];
    return this.createFormSubmitted() && !!control.errors?.['maxlength'];
  }

  protected shouldShowCreateFieldError(controlName: VendorTypeFormControlName): boolean {
    return this.createFormSubmitted() && this.createVendorTypeForm.controls[controlName].invalid;
  }

  protected shouldShowEditRequiredError(controlName: VendorTypeFormControlName): boolean {
    const control = this.editVendorTypeForm.controls[controlName];
    return this.editFormSubmitted() && !!control.errors?.['required'];
  }

  protected shouldShowEditMaxLengthError(controlName: VendorTypeFormControlName): boolean {
    const control = this.editVendorTypeForm.controls[controlName];
    return this.editFormSubmitted() && !!control.errors?.['maxlength'];
  }

  protected shouldShowEditFieldError(controlName: VendorTypeFormControlName): boolean {
    return this.editFormSubmitted() && this.editVendorTypeForm.controls[controlName].invalid;
  }

  protected loadVendorTypes(snapshot?: DatatableViewStateSnapshot): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.vendorTypeService.getVendorTypes().subscribe({
      next: (rows) => {
        this.vendorTypes.set(rows);
        this.isLoading.set(false);
        this.datatableController.initialize(snapshot);
      },
      error: () => {
        this.errorMessage.set('Unable to load vendor type data from the API.');
        this.isLoading.set(false);
      },
    });
  }

  protected onCreateVendorType(): void {
    this.createFormSubmitted.set(true);
    this.createFormError.set('');

    if (this.createVendorTypeForm.invalid) {
      this.createVendorTypeForm.markAllAsTouched();
      return;
    }

    this.isCreating.set(true);

    this.vendorTypeService.createVendorType(this.toPayload(this.createVendorTypeForm.getRawValue())).subscribe({
      next: () => {
        this.isCreating.set(false);
        this.modalController.close(this.createModalId);
        this.resetCreateVendorTypeForm();
        this.vendorTypeService.invalidateVendorTypeOptions();
        window.KTToast?.show({
          message: 'Vendor type created successfully.',
          variant: 'success',
        });
        this.loadVendorTypes();
      },
      error: () => {
        this.isCreating.set(false);
        this.createFormError.set('Unable to create vendor type at this time. Please try again.');
      },
    });
  }

  protected resetCreateVendorTypeForm(): void {
    this.createVendorTypeForm.reset(this.getEmptyFormValue());
    this.createFormSubmitted.set(false);
    this.createFormError.set('');
  }

  protected openEditVendorTypeModal(vendorType: VendorType): void {
    this.editingVendorTypeId.set(vendorType.id);
    this.editVendorTypeForm.reset({
      code: vendorType.code,
      name: vendorType.name,
      description: vendorType.description,
      isActive: vendorType.isActive,
    });
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
    this.modalController.open(this.editModalId);
  }

  protected onUpdateVendorType(): void {
    this.editFormSubmitted.set(true);
    this.editFormError.set('');

    if (this.editVendorTypeForm.invalid) {
      this.editVendorTypeForm.markAllAsTouched();
      return;
    }

    const vendorTypeId = this.editingVendorTypeId();
    if (vendorTypeId === null) {
      this.editFormError.set('Unable to identify the vendor type to update.');
      return;
    }

    const datatableSnapshot = this.datatableController.captureViewState();
    this.isUpdating.set(true);

    this.vendorTypeService.updateVendorType(vendorTypeId, this.toPayload(this.editVendorTypeForm.getRawValue())).subscribe({
      next: () => {
        this.isUpdating.set(false);
        this.modalController.close(this.editModalId);
        this.resetEditVendorTypeForm();
        this.vendorTypeService.invalidateVendorTypeOptions();
        window.KTToast?.show({
          message: 'Vendor type updated successfully.',
          variant: 'success',
        });
        this.loadVendorTypes(datatableSnapshot);
      },
      error: () => {
        this.isUpdating.set(false);
        this.editFormError.set('Unable to update vendor type at this time. Please try again.');
      },
    });
  }

  protected resetEditVendorTypeForm(): void {
    this.editVendorTypeForm.reset(this.getEmptyFormValue());
    this.editingVendorTypeId.set(null);
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
  }

  protected onTableActionClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const editButton = target?.closest('button[data-action="edit-vendor-type"]') as HTMLButtonElement | null;
    const deleteButton = target?.closest('button[data-action="delete-vendor-type"]') as HTMLButtonElement | null;

    if (editButton && !editButton.disabled) {
      const vendorTypeId = this.getVendorTypeIdFromButton(editButton);
      const vendorType = vendorTypeId === null
        ? undefined
        : this.vendorTypes().find((item) => item.id === vendorTypeId);

      if (vendorType) {
        this.openEditVendorTypeModal(vendorType);
      }

      return;
    }

    if (!deleteButton || deleteButton.disabled) {
      return;
    }

    const vendorTypeId = this.getVendorTypeIdFromButton(deleteButton);
    if (vendorTypeId === null) {
      return;
    }

    this.pendingDeleteVendorTypeId.set(vendorTypeId);
    this.modalController.open(this.deleteConfirmModalId);
  }

  protected cancelDeleteVendorType(): void {
    this.pendingDeleteVendorTypeId.set(null);
    this.modalController.close(this.deleteConfirmModalId);
  }

  protected confirmDeleteVendorType(): void {
    const vendorTypeId = this.pendingDeleteVendorTypeId();

    if (vendorTypeId === null || this.deletingVendorTypeId() !== null) {
      return;
    }

    this.modalController.close(this.deleteConfirmModalId);
    this.pendingDeleteVendorTypeId.set(null);
    this.removeVendorType(vendorTypeId);
  }

  protected removeVendorType(vendorTypeId: number): void {
    if (this.deletingVendorTypeId() !== null) {
      return;
    }

    this.deletingVendorTypeId.set(vendorTypeId);

    this.vendorTypeService.deleteVendorType(vendorTypeId).subscribe({
      next: (result) => {
        this.deletingVendorTypeId.set(null);

        if (result.kind === 'success') {
          const snapshot = this.datatableController.captureViewState();
          this.vendorTypes.update((rows) => rows.filter((item) => item.id !== vendorTypeId));
          this.vendorTypeService.invalidateVendorTypeOptions();
          this.showSuccessAlert('Vendor type deleted successfully.');
          this.datatableController.initialize(snapshot);
          return;
        }

        this.showDestructiveAlert('Unable to delete vendor type.', result.message);
      },
      error: () => {
        this.deletingVendorTypeId.set(null);
        this.showDestructiveAlert(
          'Unable to delete vendor type.',
          'Unable to delete vendor type at this time. Please try again.',
        );
      },
    });
  }

  protected dismissAlert(): void {
    this.alertVariant.set(null);
    this.alertTitle.set('');
    this.alertMessage.set('');
  }

  private showSuccessAlert(message: string): void {
    this.alertVariant.set('success');
    this.alertTitle.set(message);
    this.alertMessage.set('');
  }

  private showDestructiveAlert(title: string, message: string): void {
    this.alertVariant.set('destructive');
    this.alertTitle.set(title);
    this.alertMessage.set(message);
  }

  private getVendorTypeIdFromButton(button: HTMLButtonElement): number | null {
    const raw = button.getAttribute('data-vendor-type-id');
    const vendorTypeId = raw ? Number(raw) : Number.NaN;

    return Number.isFinite(vendorTypeId) ? vendorTypeId : null;
  }

  private getEmptyFormValue(): VendorTypeFormValue {
    return {
      code: '',
      name: '',
      description: '',
      isActive: true,
    };
  }

  private toPayload(rawValue: VendorTypeFormValue): UpsertVendorTypeRequest {
    const description = rawValue.description.trim();

    return {
      code: rawValue.code.trim().toUpperCase(),
      name: rawValue.name.trim(),
      description: description ? description : null,
      isActive: rawValue.isActive,
    };
  }
}
